import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  Signal,
} from '@angular/core';
import { ControlEvent, FormControl, FormGroup } from '@angular/forms';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { IAccessibleImage, INormalText } from '@web/dtos';
import { ITicketTileForm } from '../../../../entities/sortiment-form.entity';
import { IAccessibleImageForm } from '../../../../entities/image-entity';
import {
  ITicketTile,
  TicketTileComponent,
} from '../../../../components/data-display/ticket-tile/ticket-tile.component';
import { ToTicketTilePipe } from '../../../../pipes/to-ticket-tile/to-ticket-tile.pipe';
import { CmsTextProviderService } from '../../../../services/cms-text-provider.service';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { SectionHeadlineComponent } from '../../../../components/layout/section/section-header/section-headline/section-headline.component';

@Component({
  selector: 'aw-sortiment-preview',
  imports: [TicketTileComponent, SectionHeadlineComponent],
  templateUrl: './sortiment-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SortimentPreviewComponent {
  private static readonly PREVIEW_PRICE_IN_CENTS: number = 1200;

  public readonly ticketForm: InputSignal<FormGroup<ITicketTileForm>> =
    input.required<FormGroup<ITicketTileForm>>();
  public readonly ticketName: InputSignal<string> = input.required<string>();
  public readonly isRelationTicket: InputSignal<boolean> =
    input.required<boolean>();
  public readonly buttonTextControl: InputSignal<FormControl<INormalText[]>> =
    input.required<FormControl<INormalText[]>>();
  public readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  private readonly languagePipe: LanguagePipe = inject(LanguagePipe);
  private readonly toTicketTilePipe: ToTicketTilePipe = new ToTicketTilePipe();
  private readonly ticketFormEvent: Signal<ControlEvent<unknown> | undefined> =
    toSignal(
      toObservable(this.ticketForm).pipe(
        switchMap((form: FormGroup<ITicketTileForm>) => form.events)
      )
    );
  private readonly buttonTextEvent: Signal<ControlEvent<unknown> | undefined> =
    toSignal(
      toObservable(this.buttonTextControl).pipe(
        switchMap((control: FormControl<INormalText[]>) => control.events)
      )
    );

  protected readonly previewTicket: Signal<ITicketTile> = computed(
    (): ITicketTile => {
      this.ticketFormEvent();
      const formValue = this.ticketForm().getRawValue();
      const ticket: ITicketTile = this.toTicketTilePipe.transform(
        formValue,
        this.ticketName()
      );

      return {
        ...ticket,
        via: this.isRelationTicket() ? ticket.via : [],
        image: formValue.displayImage
          ? this.createAccessibleImage(formValue.accessibleImage)
          : undefined,
        price: formValue.displayPrice
          ? SortimentPreviewComponent.PREVIEW_PRICE_IN_CENTS
          : undefined,
      };
    }
  );

  protected readonly buttonText: Signal<string | undefined> = computed(
    (): string | undefined => {
      this.buttonTextEvent();
      return (
        this.languagePipe
          .transform(this.buttonTextControl().getRawValue())
          .trim() || undefined
      );
    }
  );

  private createAccessibleImage(
    image: ReturnType<FormGroup<IAccessibleImageForm>['getRawValue']>
  ): IAccessibleImage | undefined {
    if (!image.image) {
      return undefined;
    }

    return {
      id: image.id ?? undefined,
      image: image.image,
      imageAlts: image.imageAlts,
      url: image.url,
      positionOrder: image.positionOrder ?? 0,
    };
  }
}
